<template>
    <div class="all-okrs">
        <b-card>
            <b-row align-v="center" class="mb-4">
                <b-col cols="4" class="position-relative">
                    <img src="@/assets/icons/search.svg" class="position-absolute text-muted ml-2" style="top: 40%" />
                    <b-form-input
                        v-model="searchString"
                        :placeholder="$t('okr.search')"
                        @input="debouncedGetAllOkrsFromApi"
                        class="search-input"
                        style="padding-left: 1.9rem"
                    />
                </b-col>
                <b-col cols="4" align-v="center">
                    <periods
                        :hideSelectedOptions="false"
                        :numberOfSelectedOptionsToDisplay="3"
                        :state="null"
                        :value.sync="selectedPeriods"
                        :placeholder="$t('form.placeholder.selectPeriods')"
                        applyScopeNotArchived
                    />
                </b-col>
                <b-col>
                    <b-dropdown toggle-class="filters-dropdown" right no-caret>
                        <template slot="button-content">
                            {{ $t('form.button.filters') }}
                            <img src="@/assets/icons/filter.svg" />
                        </template>

                        <b-dropdown-form class="p-4" style="min-width: 500px">
                            <levels
                                class="pb-2"
                                :value.sync="selectedLevels"
                                @update:value="debouncedGetAllOkrsFromApi"
                                :label="$t('form.label.objectiveLevel')"
                                :placeholder="$t('form.placeholder.selectLevel')"
                                :hideSelectedOptions="false"
                            />
                            <sections
                                :label="$t('form.label.departmentAndUnit')"
                                :value.sync="selectedSections"
                                :loadDataWhenMounted="true"
                                @change="debouncedGetAllOkrsFromApi"
                                class="pb-2"
                            />
                            <Responsible
                                :label="$t('form.label.owner')"
                                :value.sync="responsibles"
                                @update:value="debouncedGetAllOkrsFromApi"
                                :placeholder="$t('form.placeholder.selectResponsible')"
                                :showLabels="true"
                                :hideSelected="false"
                                multiple
                            />
                        </b-dropdown-form>
                    </b-dropdown>
                </b-col>
                <b-col class="mt-1">
                    <download-btn
                        v-if="userHasRolePermission(['god', 'administrator', 'local_administrator', 'is_okr_leader'])"
                    />
                </b-col>
                <b-col class="d-flex justify-content-end mb-1">
                    <widget-create-object :user="user" @objectiveCreated="getAllOkrsFromApi" />
                </b-col>
            </b-row>
            <b-col v-if="loading" class="d-flex justify-content-center mt-5">
                <b-spinner
                    variant="primary"
                />
                </b-col>
            <Okrs
                v-else
                :objectives="objectives"
                @onCreatedKeyResult="getAllOkrsFromApi"
                @onDeletedKeyResult="getAllOkrsFromApi"
                @onCreatedObjective="getAllOkrsFromApi"
                @onDeletedObjective="getAllOkrsFromApi"
                @onChangeInitiative="getAllOkrsFromApi"
                :pagination.sync="pagination"
                @onChangePerPage="onChangePerPage"
                @onChangePage="getAllOkrsFromApi"
                :filters="filters"
            />
        </b-card>
        <router-view
            @onSubmittedCheckin="getAllOkrsFromApi"
            @onUpdatedKeyResult="getAllOkrsFromApi"
            @onUpdatedObjective="getAllOkrsFromApi"
            @onCreatedObjective="getAllOkrsFromApi"
        />
    </div>
</template>

<script>
import NewKeyResult from '@/views/okr/key-result/new'
import { WidgetCreateObject } from '@/views/profile/components'
import { Periods } from '@/views/components/fields'
import { Okrs } from '@/views/okr/all-okrs/components'
import { SectionsWithoutLevel as Sections, Levels, Users as Responsible } from '@/views/components/fields'
import { mapState } from 'vuex'
import { getUserFromAPI } from '@/views/profile/api'
import { DownloadBtn } from '@/views/okr/components'
import { userPermissionsMixin } from '@/mixins'
import { debounce } from 'lodash'

export default {
    name: 'AllOkrs',
    components: {
        NewKeyResult,
        Okrs,
        WidgetCreateObject,
        Periods,
        Sections,
        Levels,
        Responsible,
        DownloadBtn,
    },
    mixins: [userPermissionsMixin],
    created() {
        this.debouncedGetAllOkrsFromApi = debounce(this.getAllOkrsFromApi, 1500)
    },
    computed: {
        ...mapState('currentUser', ['user']),
        user() {
            return this.$store.state.currentUser.user
        },
        objectives() {
            return this.$store.getters['getAllOKRs']
        },
        sectionsIDs() {
            return this.selectedSections.map((section) => section.value)
        },
        levels() {
            return this.selectedLevels.map((level) => level.value)
        },
        responsiblesIds() {
            return this.responsibles.map((responsible) => responsible.value)
        },
        filters() {
            return {
                sections_ids: this.sectionsIDs,
                levels: this.levels,
                search_string: this.searchString,
                users_ids: this.responsiblesIds,
            }
        },
    },
    watch: {
        selectedPeriods() {
            this.$store.dispatch('applyPeriodsFilter', {
                periods: this.selectedPeriods,
                userId: this.user.id,
            })
            this.debouncedGetAllOkrsFromApi()
        },
    },
    data() {
        return {
            selectedPeriods: [],
            searchString: '',
            selectedSections: [],
            selectedLevels: [],
            responsibles: [],
            pagination: {
                currentPage: 1,
                perPage: 10,
                totalItems: 0,
            },
            debouncedGetAllOkrsFromApi: null,
            loading: true,
        }
    },
    mounted() {
        getUserFromAPI(this.user.id)
            .then((resp) => {
                this.$store.dispatch('profile/setUser', resp.data.data)
            })
            .catch((err) => {
                console.log(err)
            })
    },
    methods: {
        async getAllOkrsFromApi() {
            this.loading = true
            this.pagination.totalItems = await this.$store.dispatch('getAllOKRsFromAPI', {
                page: this.pagination.currentPage,
                perPage: this.pagination.perPage,
                filters: this.filters,
            })
            this.loading = false
        },
        setPaginationCurrentPage(currentPage) {
            this.pagination.currentPage = currentPage
        },
        onChangePerPage() {
            this.setPaginationCurrentPage(1)
            this.getAllOkrsFromApi()
        },
        userHasRolePermission(availableRoles) {
            const rolesPermission = this.userHasPermission(this.$store.state.currentUser.user, availableRoles, [])
            return rolesPermission
        },
    },
}
</script>

<style scoped>
.row {
    display: flex;
    justify-content: space-between;
}

.form-group {
    margin-bottom: 0;
}

.form-group legend {
    display: none;
}

.search-input {
    margin-top: 0.48rem !important;
    height: 2.75rem !important;
    border: 1px solid #a3a3a3;
}

::v-deep .filters-dropdown {
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    gap: 0.3rem;
    margin-top: 0.36rem;
    height: 2.6rem !important;
    color: white;
    font-family: 'Roboto';
    font-style: normal;
    font-weight: 600;
    background: #a3a3a3;
    border-radius: 4px;
}

::v-deep .dropdown-menu {
    margin-top: 0.8rem;
}

::v-deep .widget-create-object {
    margin-top: 0.36rem !important;
}

@media only screen and (max-width: 1800px) {
    ::v-deep .row.kr .status-counter {
        margin-left: 0.2rem;
    }
}

@media only screen and (min-width: 1900px) {
    ::v-deep .row.kr .status-counter {
        margin-right: 0.1rem;
    }
}
</style>
